// frontend/src/pages/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  ArrowRight, 
  ShoppingCart, 
  Heart, 
  Share, 
  Star, 
  StarHalf,
  Eye,
  Calendar,
  Truck,
  Shield,
  RotateCcw,
  Phone,
  Mail,
  ChevronLeft,
  ChevronRight,
  Check,
  X
} from 'lucide-react';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeTab, setActiveTab] = useState('description');

  useEffect(() => {
    fetchProductData();
  }, [slug]);

  const fetchProductData = async () => {
    try {
      setLoading(true);
      
      // دریافت اطلاعات محصول
      const productResponse = await apiService.getProduct(slug);
      const products = productResponse.data.results || productResponse.data;
      
      if (products.length === 0) {
        throw new Error('Product not found');
      }

      const productData = products[0];
      setProduct(productData);

      // دریافت محصولات مرتبط (بر اساس دسته‌بندی)
      const relatedResponse = await apiService.getProducts({
        category: productData.category?.slug,
        exclude: productData.id
      });
      setRelatedProducts(relatedResponse.data.results || relatedResponse.data.slice(0, 4));

    } catch (error) {
      console.error('Error fetching product data:', error);
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    // منطق اضافه کردن به سبد خرید
    alert(`محصول "${product.name}" به سبد خرید اضافه شد`);
  };

  const handleContact = () => {
    // منطق تماس برای محصولات بدون قیمت
    window.open(`tel:02112345678`, '_self');
  };

  const handleInquiry = () => {
    // منطق استعلام قیمت
    window.location.href = `mailto:info@businesspro.ir?subject=استعلام قیمت ${product.name}&body=سلام، لطفاً قیمت محصول "${product.name}" را اعلام کنید.`;
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const nextImage = () => {
    if (product.images && product.images.length > 0) {
      setSelectedImage((prev) => 
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (product.images && product.images.length > 0) {
      setSelectedImage((prev) => 
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  };

  const renderStars = (rating = 5) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />);
    }

    if (hasHalfStar) {
      stars.push(<StarHalf key="half" className="w-5 h-5 text-yellow-400 fill-current" />);
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-5 h-5 text-gray-300" />);
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <X className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">محصول یافت نشد</h2>
          <Link to="/products" className="text-yellow-400 hover:text-yellow-300">
            بازگشت به محصولات
          </Link>
        </div>
      </div>
    );
  }

  const mainImage = product.images && product.images.length > 0 
    ? getFullImageUrl(product.images[selectedImage]?.image)
    : getFullImageUrl(product.main_image);

  const getProductSchema = () => {
    if (!product) return null;

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.short_description,
      "image": getFullImageUrl(product.main_image),
      "sku": product.id.toString(),
      "brand": {
        "@type": "Brand",
        "name": "BusinessPro"
      },
      "offers": {
        "@type": "Offer",
        "url": window.location.href,
        "priceCurrency": "IRR",
        "price": product.price?.toString() || "0",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "BusinessPro"
        }
      }
    };
  };

  return (
    <>
      {product && (
        <SEOHead 
          title={`${product.name} - تکنو پلیمر اسپادانا`}
          description={product.short_description}
          keywords={`${product.name}, ${product.category?.name}قالب سازی، لاستیک سازی، طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی، `}
        //   image={getFullImageUrl(product.main_image)}
          type="product"
          article={null}
        >
          {/* Structured Data برای محصول */}
          <script type="application/ld+json">
            {JSON.stringify(getProductSchema())}
          </script>
        </SEOHead>
      )}
    <div className="min-h-screen bg-black py-8">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 space-x-reverse text-sm text-gray-400 mb-8">
          <Link to="/" className="hover:text-white transition-colors">خانه</Link>
          <ArrowRight size={16} />
          <Link to="/products" className="hover:text-white transition-colors">محصولات</Link>
          <ArrowRight size={16} />
          {product.category && (
            <>
              <Link 
                to={`/products?category=${product.category.slug}`} 
                className="hover:text-white transition-colors"
              >
                {product.category.name}
              </Link>
              <ArrowRight size={16} />
            </>
          )}
          <span className="text-yellow-400">{product.name}</span>
        </nav>

        {/* Product Main Section */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 md:p-8">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative bg-gray-800 rounded-2xl overflow-hidden">
                <img
                  src={mainImage}
                  alt={product.name}
                  className="w-full h-80 md:h-96 object-cover"
                />
                
                {/* Navigation Arrows */}
                {product.images && product.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                    >
                      <ChevronRight size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                    >
                      <ChevronLeft size={24} />
                    </button>
                  </>
                )}

                {/* Favorite Button */}
                <button
                  onClick={toggleFavorite}
                  className={`absolute top-4 left-4 p-2 rounded-full bg-black bg-opacity-50 transition-colors ${
                    isFavorite ? 'text-red-500' : 'text-gray-300 hover:text-red-500'
                  }`}
                >
                  <Heart fill={isFavorite ? 'currentColor' : 'none'} size={24} />
                </button>

                {/* Featured Badge */}
                {product.is_featured && (
                  <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold">
                    محصول ویژه
                  </div>
                )}
              </div>

              {/* Thumbnail Images */}
              {product.images && product.images.length > 1 && (
                <div className="flex space-x-3 space-x-reverse overflow-x-auto pb-2">
                  {product.images.map((image, index) => (
                    <button
                      key={image.id}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index 
                          ? 'border-yellow-400' 
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <img
                        src={getFullImageUrl(image.image)}
                        alt={`${product.name} - تصویر ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {product.name}
                </h1>
                
                <div className="flex items-center space-x-4 space-x-reverse mb-4">
                  {/* Rating */}
                  <div className="flex items-center space-x-1 space-x-reverse">
                    {renderStars()}
                    <span className="text-gray-400 text-sm mr-2">(۱۲ نظر)</span>
                  </div>
                  
                  {/* View Count */}
                  <div className="flex items-center space-x-1 space-x-reverse text-gray-400 text-sm">
                    <Eye size={16} />
                    <span>۱,۲۴۷ بازدید</span>
                  </div>
                </div>

                {/* Category */}
                {product.category && (
                  <div className="flex items-center space-x-2 space-x-reverse text-gray-400 mb-4">
                    <span>دسته‌بندی:</span>
                    <Link 
                      to={`/products?category=${product.category.slug}`}
                      className="text-yellow-400 hover:text-yellow-300"
                    >
                      {product.category.name}
                    </Link>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                {product.price ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <span className="text-3xl font-bold text-yellow-400">
                        {product.price.toLocaleString()} تومان
                      </span>
                      {product.old_price && (
                        <span className="text-xl text-gray-400 line-through">
                          {product.old_price.toLocaleString()} تومان
                        </span>
                      )}
                    </div>
                    {product.old_price && (
                      <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold inline-block">
                        {Math.round((1 - product.price / product.old_price) * 100)}% تخفیف
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-gray-300 text-lg mb-4">قیمت این محصول متغیر است</p>
                    <p className="text-gray-400 text-sm">برای استعلام قیمت با ما تماس بگیرید</p>
                  </div>
                )}
              </div>

              {/* Short Description */}
              <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                <h3 className="font-bold text-white mb-3">توضیحات کوتاه</h3>
                <p className="text-gray-300 leading-relaxed">
                  {product.short_description}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                {product.price ? (
                  <>
                    {/* Quantity Selector */}
                    <div className="flex items-center space-x-4 space-x-reverse">
                      <span className="text-gray-300">تعداد:</span>
                      <div className="flex items-center space-x-2 space-x-reverse bg-gray-800 rounded-lg p-1">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                        >
                          -
                        </button>
                        <span className="w-8 text-center text-white font-medium">{quantity}</span>
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={handleAddToCart}
                        className="flex-1 bg-yellow-400 text-gray-900 py-4 px-6 rounded-xl font-bold hover:bg-yellow-300 transition-all flex items-center justify-center space-x-2 space-x-reverse"
                      >
                        <ShoppingCart size={20} />
                        <span>افزودن به سبد خرید</span>
                      </button>
                      
                      <button className="flex-1 border-2 border-yellow-400 text-yellow-400 py-4 px-6 rounded-xl font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all flex items-center justify-center space-x-2 space-x-reverse">
                        <Phone size={20} />
                        <span>سفارش سریع</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleContact}
                      className="flex-1 bg-yellow-400 text-gray-900 py-4 px-6 rounded-xl font-bold hover:bg-yellow-300 transition-all flex items-center justify-center space-x-2 space-x-reverse"
                    >
                      <Phone size={20} />
                      <span>تماس برای قیمت</span>
                    </button>
                    
                    <button
                      onClick={handleInquiry}
                      className="flex-1 border-2 border-yellow-400 text-yellow-400 py-4 px-6 rounded-xl font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all flex items-center justify-center space-x-2 space-x-reverse"
                    >
                      <Mail size={20} />
                      <span>استعلام قیمت</span>
                    </button>
                  </div>
                )}

                {/* Additional Actions */}
                <div className="flex items-center justify-center space-x-6 space-x-reverse text-gray-400">
                  <button className="flex items-center space-x-2 space-x-reverse hover:text-white transition-colors">
                    <Share size={18} />
                    <span>اشتراک‌گذاری</span>
                  </button>
                  <button 
                    onClick={toggleFavorite}
                    className={`flex items-center space-x-2 space-x-reverse transition-colors ${
                      isFavorite ? 'text-red-500' : 'hover:text-white'
                    }`}
                  >
                    <Heart fill={isFavorite ? 'currentColor' : 'none'} size={18} />
                    <span>{isFavorite ? 'حذف از علاقه‌مندی‌ها' : 'افزودن به علاقه‌مندی‌ها'}</span>
                  </button>
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2 space-x-reverse text-gray-300">
                  <Truck size={16} className="text-green-400" />
                  <span>ارسال رایگان</span>
                </div>
                <div className="flex items-center space-x-2 space-x-reverse text-gray-300">
                  <Shield size={16} className="text-blue-400" />
                  <span>گارانتی ۱۸ ماهه</span>
                </div>
                <div className="flex items-center space-x-2 space-x-reverse text-gray-300">
                  <RotateCcw size={16} className="text-purple-400" />
                  <span>بازگشت ۷ روزه</span>
                </div>
                <div className="flex items-center space-x-2 space-x-reverse text-gray-300">
                  <Check size={16} className="text-green-400" />
                  <span>موجود در انبار</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 mb-8">
          {/* Tab Navigation */}
          <div className="border-b border-gray-800">
            <nav className="flex space-x-8 space-x-reverse px-6 overflow-x-auto">
              {[
                { id: 'description', label: 'توضیحات کامل' },
                { id: 'specifications', label: 'مشخصات فنی' },
                { id: 'reviews', label: 'نظرات کاربران' },
                { id: 'shipping', label: 'ارسال و گارانتی' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 py-4 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-yellow-500 text-yellow-400'
                      : 'border-transparent text-gray-400 hover:text-white'
                  }`}
                >
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'description' && (
              <div className="prose prose-invert max-w-none">
                <div 
                  className="text-gray-300 leading-8"
                  dangerouslySetInnerHTML={{ __html: product.full_description || product.short_description }}
                />
              </div>
            )}

            {activeTab === 'specifications' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <span className="text-gray-400">برند:</span>
                    <span className="text-white mr-2">BusinessPro</span>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <span className="text-gray-400">مدل:</span>
                    <span className="text-white mr-2">BP-2024</span>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <span className="text-gray-400">گارانتی:</span>
                    <span className="text-white mr-2">۱۸ ماه</span>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <span className="text-gray-400">ساخت:</span>
                    <span className="text-white mr-2">ایران</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="text-center py-8">
                <Star className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">هنوز نظری برای این محصول ثبت نشده است.</p>
                <button className="mt-4 text-yellow-400 hover:text-yellow-300">
                  اولین نفری باشید که نظر می‌دهد
                </button>
              </div>
            )}

            {activeTab === 'shipping' && (
              <div className="space-y-4">
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="font-bold text-white mb-3">🚚 اطلاعات ارسال</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• ارسال رایگان برای خریدهای بالای ۵۰۰,۰۰۰ تومان</li>
                    <li>• ارسال به تمامی نقاط کشور</li>
                    <li>• زمان تحویل: ۲ تا ۴ روز کاری</li>
                    <li>• امکان پیگیری مرسوله</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="font-bold text-white mb-3">🛡️ گارانتی و بازگشت</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• گارانتی ۱۸ ماهه BusinessPro</li>
                    <li>• پشتیبانی ۲۴ ساعته</li>
                    <li>• امکان بازگشت کالا تا ۷ روز</li>
                    <li>• خدمات پس از فروش در سراسر کشور</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
            <h2 className="text-2xl font-bold text-white mb-6">محصولات مرتبط</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/products/${relatedProduct.slug}`}
                  className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all group"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={getFullImageUrl(relatedProduct.main_image)}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    {relatedProduct.is_featured && (
                      <div className="absolute top-2 left-2 bg-yellow-400 text-gray-900 px-2 py-1 rounded text-xs font-bold">
                        ویژه
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-bold text-white text-sm mb-2 line-clamp-2 group-hover:text-yellow-400 transition-colors">
                      {relatedProduct.name}
                    </h3>
                    {relatedProduct.price && (
                      <div className="text-yellow-400 font-bold text-sm">
                        {relatedProduct.price.toLocaleString()} تومان
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ProductDetail;